import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AlertCircle, Clock, Plus, Trash2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  EVENT_REMINDER_DAY_MAX_VALUE,
  EVENT_REMINDER_HOUR_MAX_VALUE,
  EVENT_REMINDER_MINUTE_MAX_VALUE,
  EVENT_REMINDER_WEEK_MAX_VALUE,
  REMINDER_TYPE_CUSTOM,
  UNIT_DAY,
  UNIT_HOUR,
  UNIT_MINUTE,
  UNIT_WEEK,
} from "@/constants";
import { EventInfoDialog } from "@/features/study-group-events/components/EventInfoDialog";
import { NewReminderForm } from "@/features/study-group-events/components/NewReminderForm";
import { ReminderEmailEditor } from "@/features/study-group-events/components/ReminderEmailEditor";
import { ScopeSelectionContent } from "@/features/study-group-events/components/ScopeSelectionModal";
import { buildEventInfoItems } from "@/features/study-group-events/utils/eventInfo";
import { usePropagateReminders } from "@/hooks/usePropagateReminders";
import { useReminderEmailForm } from "@/hooks/useReminderEmailForm";
import { toast } from "@/hooks/useToast";
import { useUpdateAllParticipantsReminders } from "@/hooks/useUpdateAllParticipantsReminders";
import { eventsApi } from "@/lib/humhub-api/events";
import type {
  EventApiResponse,
  RecurrenceScope,
  Reminder,
  UserShort,
} from "@/lib/humhub-api/types";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";
import { formatRecurrencePattern } from "@/lib/utils/event/recurrence";
import { formatReminderText } from "@/lib/utils/eventUtils";
import {
  checkDuplicateReminder,
  REMINDER_CONSTRAINTS,
} from "@/lib/utils/reminderValidation";

const formatTimeUntilEventLabel = (
  value: number,
  unit: number,
  t: ReturnType<typeof useTranslation>["t"],
) => {
  const key =
    unit === UNIT_MINUTE
      ? "minute"
      : unit === UNIT_HOUR
        ? "hour"
        : unit === UNIT_DAY
          ? "day"
          : "week";
  const suffix = value === 1 ? "one" : "other";
  const unitText = t(`common.${key}_${suffix}`, { count: value });
  return `${value} ${unitText}`;
};

type ReminderWithTime = Pick<Reminder, "unit" | "value"> & Partial<Reminder>;

const toEventReminderPayload = (reminder: ReminderWithTime) => ({
  type: reminder.type ?? REMINDER_TYPE_CUSTOM,
  unit: reminder.unit,
  value: reminder.value,
  email_subject: reminder.email_subject,
  email_body_html: reminder.email_body_html,
  email_body_text: reminder.email_body_text,
});

const toParticipantReminderPayload = (reminder: ReminderWithTime) => ({
  unit: reminder.unit,
  value: reminder.value,
  email_subject: reminder.email_subject,
  email_body_html: reminder.email_body_html,
  email_body_text: reminder.email_body_text,
});

interface ReminderEmailPanelProps {
  eventId: number;
  eventTitle: string;
  eventDescription?: string | null;
  eventDatetimeLabel?: string;
  eventUrl?: string;
  primaryImageGuid?: string | null;
  reminders?: Reminder[];
  participants?: UserShort[];
  showImageButton?: boolean;
  isOpen?: boolean;
  onRemindersUpdated?: (scope?: RecurrenceScope) => void;
  eventData: EventApiResponse;
  scope?: "reminder" | "entry";
  defaultSubject?: string;
  defaultBodyText?: string;
  defaultBodyHtml?: string | null;
  reminderId?: number | null;
  onDirtyChange?: (dirty: boolean) => void;
  onReminderChange?: (reminderId: number | null) => void;
  onScopeChange?: (scope: "reminder" | "entry") => void;
  reminderSelectValue?: string;
  onReminderSelect?: (value: string) => void;
  hasRecurrence?: boolean;
}

export const ReminderEmailPanel = ({
  eventId,
  eventTitle,
  eventDescription,
  eventDatetimeLabel,
  eventUrl,
  primaryImageGuid,
  reminders = [],
  showImageButton = true,
  eventData,
  onRemindersUpdated,
  hasRecurrence = false,
}: ReminderEmailPanelProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isEventInfoDialogOpen, setIsEventInfoDialogOpen] = useState(false);
  const [deletingReminderId, setDeletingReminderId] = useState<number | null>(
    null,
  );
  const [confirmDeleteReminderId, setConfirmDeleteReminderId] = useState<
    number | null
  >(null);
  const [isScopeDialogOpen, setIsScopeDialogOpen] = useState(false);
  const [selectedScope, setSelectedScope] = useState<RecurrenceScope>("single");
  const [pendingScopeAction, setPendingScopeAction] = useState<
    ((scope: RecurrenceScope) => Promise<void>) | null
  >(null);
  const [scopeActionLoading, setScopeActionLoading] = useState(false);
  const scopeOptionsForModal = useMemo<RecurrenceScope[]>(() => {
    return eventData?.is_parent === true
      ? ["single", "all"]
      : ["single", "future", "all"];
  }, [eventData?.is_parent]);
  const scopeOptionLabels = useMemo(() => {
    const recurrenceRootStart =
      eventData?.recurrence_root_start_datetime ??
      eventData?.recurrence?.start_date ??
      null;
    const startForAll = recurrenceRootStart
      ? dayjs(recurrenceRootStart).format("YYYY-MM-DD")
      : eventData?.start_datetime
        ? dayjs(eventData.start_datetime).format("YYYY-MM-DD")
        : null;
    const startForFuture = eventData?.start_datetime
      ? dayjs(eventData.start_datetime).format("YYYY-MM-DD")
      : startForAll;
    const untilRaw = eventData?.recurrence?.until;
    const until = untilRaw ? dayjs(untilRaw).format("YYYY-MM-DD") : null;
    return {
      future:
        eventData?.is_parent === true
          ? undefined
          : startForFuture && until
            ? `${t("common.scopeFuture")}`
            : startForFuture
              ? `${t("common.scopeFuture")}`
              : undefined,
      all:
        startForAll && until
          ? `${t("common.scopeAll")}`
          : startForAll
            ? `${t("common.scopeAll")}`
            : undefined,
    } as Partial<Record<RecurrenceScope, string>>;
  }, [eventData, t]);
  const scopeModalDescription = useMemo(() => {
    if (!eventData?.recurrence) return undefined;
    const summary = formatRecurrencePattern(eventData.recurrence, t);
    if (!summary) return undefined;
    const recurrenceStart =
      eventData.recurrence?.start_date ??
      (eventData.recurrence_root_start_datetime
        ? dayjs(eventData.recurrence_root_start_datetime).format("YYYY-MM-DD")
        : eventData.start_datetime
          ? dayjs(eventData.start_datetime).format("YYYY-MM-DD")
          : "");
    const recurrenceUntil =
      eventData.recurrence?.until && dayjs(eventData.recurrence.until).isValid()
        ? dayjs(eventData.recurrence.until).format("YYYY-MM-DD")
        : eventData.recurrence?.until;
    const range =
      recurrenceStart && recurrenceUntil
        ? `${recurrenceStart}~${recurrenceUntil}`
        : recurrenceStart
          ? `${recurrenceStart}~`
          : undefined;
    return t("eventManage.scopeSelectionDescriptionWithRecurrence", {
      summary,
      range: range ?? "",
    });
  }, [eventData, t]);
  const refreshEventData = useCallback(() => {
    queryInvalidationHelpers.invalidateEventQueries(queryClient);
    queryClient.invalidateQueries({ queryKey: ["eventDetail", eventId] });
  }, [eventId, queryClient]);

  const { updateAllParticipantsReminders } = useUpdateAllParticipantsReminders({
    onSuccess: () => {
      onRemindersUpdated?.();
    },
  });

  const eventInfoItems = buildEventInfoItems({
    title: eventTitle,
    description: eventDescription ?? undefined,
    datetimeLabel: eventDatetimeLabel ?? undefined,
    url: eventUrl ?? undefined,
    offlineLocation: eventData.location ?? undefined,
    translate: t,
  });

  const reminderCount = reminders.length;
  const canAddReminder = reminderCount < REMINDER_CONSTRAINTS.MAX_REMINDERS;

  const handleCopyEventInfo = async (
    value: string | undefined | null,
    label: string,
  ) => {
    if (
      !value ||
      value === "-" ||
      typeof navigator === "undefined" ||
      !navigator.clipboard
    ) {
      toast({
        title: t("common.error"),
        description: t("eventManage.reminderEmailEventInfoCopyFailed"),
        variant: "error",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      toast({
        title: t("common.success"),
        description: t("eventManage.reminderEmailEventInfoCopied", { label }),
      });
    } catch (error) {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error
            ? error.message
            : t("eventManage.reminderEmailEventInfoCopyFailed"),
        variant: "error",
      });
    }
  };

  const runWithScope = (action: (scope: RecurrenceScope) => Promise<void>) => {
    if (!hasRecurrence) {
      void action("single");
      return;
    }
    setSelectedScope("single");
    setPendingScopeAction(() => action);
    setIsScopeDialogOpen(true);
  };

  const handleScopeConfirm = async (scopeOverride?: RecurrenceScope) => {
    if (!pendingScopeAction) return;
    const scopeToUse = scopeOverride ?? selectedScope;
    try {
      setScopeActionLoading(true);
      await pendingScopeAction(scopeToUse);
    } finally {
      setScopeActionLoading(false);
      setIsScopeDialogOpen(false);
      setPendingScopeAction(null);
    }
  };

  const handleScopeCancel = () => {
    if (scopeActionLoading) return;
    setIsScopeDialogOpen(false);
    setPendingScopeAction(null);
  };

  const handleCreateReminder = async (
    newReminder: { unit: number; value: number },
    scope: RecurrenceScope,
  ) => {
    try {
      // Create reminder with email template
      await eventsApi.updateEvent(eventId, {
        CalendarEntryForm: {
          reminders: [
            ...reminders.map(toEventReminderPayload),
            {
              type: REMINDER_TYPE_CUSTOM,
              unit: newReminder.unit,
              value: newReminder.value,
            },
          ],
          scope,
        },
      });

      // Update all participants' reminders
      await updateAllParticipantsReminders(
        eventId,
        [
          ...reminders.map(toParticipantReminderPayload),
          {
            unit: newReminder.unit,
            value: newReminder.value,
          },
        ],
        scope,
      );

      refreshEventData();

      setIsAddingNew(false);
      onRemindersUpdated?.(scope);

      toast({
        title: t("common.success"),
        description: t("eventManage.reminderCreated"),
      });
    } catch (error) {
      console.error("Failed to create reminder:", error);
      toast({
        title: t("common.error"),
        description: t("eventManage.reminderCreateFailed"),
        variant: "error",
      });
    }
  };

  const handleDeleteReminder = async (
    reminderId: number,
    scope: RecurrenceScope,
  ) => {
    setDeletingReminderId(reminderId);

    try {
      const updatedReminders = reminders.filter((r) => r.id !== reminderId);

      await eventsApi.updateEvent(eventId, {
        CalendarEntryForm: {
          reminders: updatedReminders.map(toEventReminderPayload),
          scope,
        },
      });

      if (updatedReminders.length === 0) {
        // When all reminders are removed, use DELETE endpoint instead of POST
        await eventsApi.deleteAllParticipantsReminders(eventId, scope);
      } else {
        await updateAllParticipantsReminders(
          eventId,
          updatedReminders.map(toParticipantReminderPayload),
          scope,
        );
      }

      refreshEventData();

      onRemindersUpdated?.(scope);

      toast({
        title: t("common.success"),
        description: t("eventManage.reminderDeleted"),
      });
    } catch (error) {
      console.error("Failed to delete reminder:", error);
      toast({
        title: t("common.error"),
        description: t("eventManage.reminderDeleteFailed"),
        variant: "error",
      });
    } finally {
      setDeletingReminderId(null);
    }
  };

  const confirmDeleteReminder = async () => {
    if (!confirmDeleteReminderId) return;
    await handleDeleteReminder(confirmDeleteReminderId, selectedScope);
    setConfirmDeleteReminderId(null);
  };

  if (isScopeDialogOpen) {
    return (
      <ScopeSelectionContent
        isActive={isScopeDialogOpen}
        onClose={scopeActionLoading ? () => {} : handleScopeCancel}
        onConfirm={(scope) => handleScopeConfirm(scope)}
        defaultScope={selectedScope}
        loading={scopeActionLoading}
        scopes={scopeOptionsForModal}
        description={scopeModalDescription}
        scopeOptionLabels={scopeOptionLabels}
        className="min-h-[320px]"
      />
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">
              {t("eventManage.reminderAndEmailSettings")}
            </h2>
          </div>
          <Badge variant="outline">
            {t("eventManage.reminderCount", {
              current: reminderCount,
              max: REMINDER_CONSTRAINTS.MAX_REMINDERS,
            })}
          </Badge>
        </div>

        {/* Accordion */}
        <Accordion value={openItems} onValueChange={setOpenItems}>
          {/* Existing Reminders */}
          {reminders.map((reminder) => {
            if (!reminder.id) return null;

            const reminderKey = `reminder-${reminder.id}`;

            return (
              <ReminderAccordionItem
                key={reminder.id}
                reminderId={reminder.id}
                reminder={reminder}
                allReminders={reminders}
                eventId={eventId}
                eventTitle={eventTitle}
                eventLanguage={eventData.language}
                primaryImageGuid={primaryImageGuid}
                showImageButton={showImageButton}
                isDeleting={deletingReminderId === reminder.id}
                onDelete={
                  reminder.id
                    ? () =>
                        runWithScope(async (scope) => {
                          setSelectedScope(scope);
                          setConfirmDeleteReminderId(reminder.id ?? null);
                        })
                    : undefined
                }
                onAfterMutation={() => {
                  refreshEventData();
                }}
                onOpenEventInfo={() => setIsEventInfoDialogOpen(true)}
                isOpen={openItems.includes(reminderKey)}
                withScope={runWithScope}
                onRemindersUpdated={onRemindersUpdated}
                hasRecurrence={hasRecurrence}
              />
            );
          })}

          {/* New Reminder Form */}
          {isAddingNew && (
            <AccordionItem value="new">
              <AccordionTrigger>
                <div className="flex items-center gap-3">
                  <Plus className="size-5 text-green-600" />
                  <div className="text-left">
                    <div className="font-medium">
                      {t("eventManage.newReminder")}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t("eventManage.newReminderDescription")}
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <NewReminderForm
                  existingReminders={reminders}
                  onSave={(reminder) =>
                    runWithScope((scope) =>
                      handleCreateReminder(reminder, scope),
                    )
                  }
                  onCancel={() => setIsAddingNew(false)}
                />
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        {/* Add Button */}
        {canAddReminder && !isAddingNew && (
          <Button
            variant="outline"
            onClick={() => {
              setIsAddingNew(true);
              setOpenItems([...openItems, "new"]);
            }}
            className="w-full"
          >
            <Plus className="mr-2 size-4" />
            {t("eventManage.addNewReminder")}
          </Button>
        )}
      </div>

      {/* Dialogs */}
      <ConfirmationDialog
        open={confirmDeleteReminderId !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteReminderId(null);
        }}
        title={t("common.delete")}
        description={t("eventManage.reminderDeleteConfirmDescription")}
        cancelText={t("common.cancel")}
        confirmText={t("common.delete")}
        onConfirm={confirmDeleteReminder}
        isLoading={deletingReminderId !== null}
      />
      <EventInfoDialog
        open={isEventInfoDialogOpen}
        items={eventInfoItems}
        onOpenChange={setIsEventInfoDialogOpen}
        onCopy={handleCopyEventInfo}
      />
    </>
  );
};

// Individual Reminder Accordion Item Component
interface ReminderAccordionItemProps {
  reminderId: number;
  reminder: Reminder;
  allReminders: Reminder[];
  eventId: number;
  eventTitle: string;
  eventLanguage?: string | null;
  primaryImageGuid?: string | null;
  showImageButton?: boolean;
  isDeleting: boolean;
  isOpen: boolean;
  onDelete?: () => void;
  onAfterMutation?: () => void;
  onOpenEventInfo: () => void;
  withScope: (
    action: (scope: RecurrenceScope) => Promise<void>,
  ) => void | Promise<void>;
  onRemindersUpdated?: (scope?: RecurrenceScope) => void;
  hasRecurrence?: boolean;
}

const ReminderAccordionItem = ({
  reminderId,
  reminder,
  allReminders,
  eventId,
  eventTitle,
  eventLanguage,
  primaryImageGuid,
  showImageButton = true,
  isDeleting,
  isOpen,
  onDelete,
  onAfterMutation,
  onOpenEventInfo,
  withScope,
  onRemindersUpdated,
  hasRecurrence = false,
}: ReminderAccordionItemProps) => {
  const { t, i18n } = useTranslation();
  const subjectFieldId = useId();
  const bodyFieldId = useId();
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const bodyTextareaRef = useRef<HTMLDivElement>(null);
  const [loadedTemplate, setLoadedTemplate] = useState<{
    subject: string;
    bodyText: string;
    bodyHtml: string;
  } | null>(null);
  const [reminderUnit, setReminderUnit] = useState(reminder.unit);
  const [reminderValue, setReminderValue] = useState(reminder.value);
  const [inputValue, setInputValue] = useState(String(reminder.value));
  const [timeValidationError, setTimeValidationError] = useState<string | null>(
    null,
  );

  // Synchronize state with prop changes
  useEffect(() => {
    setReminderUnit(reminder.unit);
    setReminderValue(reminder.value);
    setInputValue(String(reminder.value));
  }, [reminder.unit, reminder.value]);

  // Helper functions for value constraints
  const getMaxValueForUnit = (unit: number): number => {
    switch (unit) {
      case UNIT_MINUTE:
        return EVENT_REMINDER_MINUTE_MAX_VALUE;
      case UNIT_HOUR:
        return EVENT_REMINDER_HOUR_MAX_VALUE;
      case UNIT_DAY:
        return EVENT_REMINDER_DAY_MAX_VALUE;
      case UNIT_WEEK:
        return EVENT_REMINDER_WEEK_MAX_VALUE;
      default:
        return EVENT_REMINDER_DAY_MAX_VALUE;
    }
  };

  const getMinValueForUnit = (unit: number): number => {
    return unit === UNIT_MINUTE ? 10 : 1;
  };

  const sanitizeValueForUnit = (unit: number, rawValue: number): number => {
    const minValue = getMinValueForUnit(unit);
    const maxValue = getMaxValueForUnit(unit);
    const fallbackValue = Number.isFinite(rawValue) ? rawValue : minValue;
    const clampedValue = Math.min(Math.max(fallbackValue, minValue), maxValue);

    if (unit === UNIT_MINUTE) {
      // Round to nearest 10
      return Math.round(clampedValue / 10) * 10;
    }

    return clampedValue;
  };

  const validateReminderTime = useCallback(
    (unit: number, value: number) => {
      const validation = checkDuplicateReminder(
        { unit, value },
        allReminders,
        reminderId,
        t,
      );
      setTimeValidationError(validation.error ?? null);
      return validation;
    },
    [allReminders, reminderId, t],
  );

  const formatUnitLabel = (unit: number, value: number) => {
    if (unit === UNIT_MINUTE) return t("common.minute", { count: value });
    if (unit === UNIT_HOUR) return t("common.hour", { count: value });
    if (unit === UNIT_DAY) return t("common.day", { count: value });
    if (unit === UNIT_WEEK) return t("common.week", { count: value });
    return "";
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const rawValue = Number.parseInt(inputValue, 10);
    const sanitizedValue = sanitizeValueForUnit(reminderUnit, rawValue);
    setReminderValue(sanitizedValue);
    setInputValue(String(sanitizedValue));
    validateReminderTime(reminderUnit, sanitizedValue);
  };

  // Fetch email template when accordion is opened
  useEffect(() => {
    if (!isOpen) return;

    const fetchTemplate = async () => {
      try {
        const template = await eventsApi.fetchCustomReminderTemplate(
          eventId,
          reminderId,
        );
        setLoadedTemplate({
          subject: template.subject ?? reminder.email_subject ?? "",
          bodyText: template.body_text ?? reminder.email_body_text ?? "",
          bodyHtml: template.body_html ?? reminder.email_body_html ?? "",
        });
      } catch (error) {
        console.error("Failed to fetch reminder template:", error);
        // Fallback to reminder data
        setLoadedTemplate({
          subject: reminder.email_subject ?? "",
          bodyText: reminder.email_body_text ?? "",
          bodyHtml: reminder.email_body_html ?? "",
        });
      }
    };

    fetchTemplate();
  }, [
    isOpen,
    reminderId,
    eventId,
    reminder.email_subject,
    reminder.email_body_text,
    reminder.email_body_html,
  ]);

  const reminderText = formatReminderText(reminder, t);
  const timeUntilTranslator = i18n.getFixedT(eventLanguage || i18n.language);
  const timeUntilEventLabel = formatTimeUntilEventLabel(
    reminderValue,
    reminderUnit,
    timeUntilTranslator,
  );

  useEffect(() => {
    if (!isOpen) return;
    validateReminderTime(reminderUnit, reminderValue);
  }, [isOpen, reminderUnit, reminderValue, validateReminderTime]);

  const {
    subject,
    preview,
    placeholders,
    isSubmitting,
    hasChanges,
    handleSubjectChange,
    handleBodyChange,
    handleInsertTimeUntilEventPlaceholder,
    handleSendTest,
    handleInsertImagePlaceholder,
    buildEmailPayload,
    markAsSaved,
  } = useReminderEmailForm({
    eventId,
    eventTitle,
    reminderId,
    scope: "reminder",
    defaultSubject: loadedTemplate?.subject ?? reminder.email_subject,
    defaultBodyText: loadedTemplate?.bodyText ?? reminder.email_body_text,
    defaultBodyHtml: loadedTemplate?.bodyHtml ?? reminder.email_body_html,
    primaryImageGuid,
    timeUntilEventLabel,
  });

  const { updateAllParticipantsReminders } = useUpdateAllParticipantsReminders({
    onSuccess: () => {
      onAfterMutation?.();
    },
  });
  const { propagateReminders } = usePropagateReminders();

  const hasTimeChanges =
    reminderUnit !== reminder.unit || reminderValue !== reminder.value;

  const handleSaveReminder = async (scope: RecurrenceScope) => {
    const timeValidation = validateReminderTime(reminderUnit, reminderValue);
    if (!timeValidation.isValid) {
      toast({
        title: t("common.error"),
        description:
          timeValidation.error ??
          t("eventManage.reminderDuplicateError", { time: "" }),
        variant: "error",
      });
      return;
    }

    const { validationError, payload } = buildEmailPayload();
    if (validationError) {
      toast({
        title: t("common.error"),
        description: validationError,
        variant: "error",
      });
      return;
    }

    try {
      // Save email template
      await eventsApi.sendReminderEmail(eventId, {
        reminderId,
        email_subject: payload.subject,
        email_body_html: payload.bodyHtml,
        email_body_text: payload.bodyText,
        save_for_reminder: true,
        save_for_entry: false,
        send_now: false,
        test_mode: false,
      });

      // Update event reminders when time has changed
      if (hasTimeChanges) {
        const updatedReminders = allReminders.map((r) => {
          const isTarget = r.id === reminderId;
          const nextUnit = isTarget ? reminderUnit : r.unit;
          const nextValue = isTarget ? reminderValue : r.value;

          return {
            type: r.type ?? REMINDER_TYPE_CUSTOM,
            unit: nextUnit,
            value: nextValue,
            email_subject: isTarget ? payload.subject : r.email_subject,
            email_body_html: isTarget ? payload.bodyHtml : r.email_body_html,
            email_body_text: isTarget ? payload.bodyText : r.email_body_text,
          };
        });

        await eventsApi.updateEvent(eventId, {
          CalendarEntryForm: {
            reminders: updatedReminders.map(toEventReminderPayload),
            scope,
          },
        });
      }

      if (hasRecurrence) {
        await propagateReminders(eventId, scope);
      }

      // Update reminder time and email for all participants
      const updatedRemindersForParticipants = allReminders.map((r) => {
        if (r.id === reminderId) {
          return {
            unit: reminderUnit,
            value: reminderValue,
            email_subject: payload.subject,
            email_body_html: payload.bodyHtml,
            email_body_text: payload.bodyText,
          };
        }
        return {
          unit: r.unit,
          value: r.value,
          email_subject: r.email_subject,
          email_body_html: r.email_body_html,
          email_body_text: r.email_body_text,
        };
      });

      await updateAllParticipantsReminders(
        eventId,
        updatedRemindersForParticipants,
        scope,
      );

      markAsSaved();
      onAfterMutation?.();
      onRemindersUpdated?.(scope);

      toast({
        title: t("common.success"),
        description: t("eventManage.reminderEmailUpdated"),
      });
    } catch (error) {
      console.error("Failed to save reminder:", error);
      toast({
        title: t("common.error"),
        description: t("eventManage.reminderUpdateFailed"),
        variant: "error",
      });
    }
  };

  return (
    <AccordionItem value={`reminder-${reminderId}`}>
      <div className="flex items-center">
        <AccordionTrigger disabled={isDeleting} className="flex-1">
          <div className="flex flex-1 items-center justify-between pr-4">
            <div className="flex items-center gap-3">
              <Clock className="size-5 text-purple-600" />
              <div className="text-left">
                <div className="font-medium">
                  {reminderText} {t("eventManage.autoSendTemplate")}
                </div>
                <div className="text-xs text-gray-500">
                  {t("eventManage.autoSendTemplateDescription", {
                    time: reminderText,
                  })}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasChanges && (
                <Badge variant="warning">
                  {t("eventManage.unsavedChanges")}
                </Badge>
              )}
            </div>
          </div>
        </AccordionTrigger>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          disabled={isDeleting}
          className="mr-4 size-8 p-0"
        >
          <Trash2 className="size-4 text-red-600" />
        </Button>
      </div>
      <AccordionContent>
        <div className="space-y-4">
          {/* Reminder Time Setting */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <Label className="mb-2 block font-medium">
              {t("eventManage.reminderTimeLabel")}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={getMinValueForUnit(reminderUnit)}
                max={getMaxValueForUnit(reminderUnit)}
                step={reminderUnit === UNIT_MINUTE ? 10 : 1}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className="w-20"
              />
              <Select
                value={String(reminderUnit)}
                onValueChange={(v) => {
                  const newUnit = Number(v);
                  const sanitized = sanitizeValueForUnit(
                    newUnit,
                    reminderValue,
                  );
                  setReminderUnit(newUnit);
                  setReminderValue(sanitized);
                  setInputValue(String(sanitized));
                  validateReminderTime(newUnit, sanitized);
                }}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(UNIT_MINUTE)}>
                    {formatUnitLabel(UNIT_MINUTE, reminderValue)}
                  </SelectItem>
                  <SelectItem value={String(UNIT_HOUR)}>
                    {formatUnitLabel(UNIT_HOUR, reminderValue)}
                  </SelectItem>
                  <SelectItem value={String(UNIT_DAY)}>
                    {formatUnitLabel(UNIT_DAY, reminderValue)}
                  </SelectItem>
                  <SelectItem value={String(UNIT_WEEK)}>
                    {formatUnitLabel(UNIT_WEEK, reminderValue)}
                  </SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-600">
                {t("eventManage.beforeEvent")}
              </span>
            </div>
            {timeValidationError && (
              <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="size-4" />
                <span>{timeValidationError}</span>
              </div>
            )}
          </div>

          {/* Email Template Editor */}
          <ReminderEmailEditor
            subjectFieldId={subjectFieldId}
            bodyFieldId={bodyFieldId}
            subject={subject}
            body={preview.bodyHtml}
            templateId={reminderId}
            isSubmitting={isSubmitting}
            hasChanges={hasChanges || hasTimeChanges}
            showImageButton={showImageButton}
            onSubjectChange={handleSubjectChange}
            onBodyChange={handleBodyChange}
            imagePlaceholderHtml={placeholders.imageHtml}
            timeUntilEventPlaceholderHtml={placeholders.timeUntilEventHtml}
            onOpenEventInfo={onOpenEventInfo}
            onOpenSendNowDialog={() => {}}
            onSendTest={handleSendTest}
            onSave={() => withScope((scope) => handleSaveReminder(scope))}
            onInsertImagePlaceholder={handleInsertImagePlaceholder}
            onInsertTimeUntilEventPlaceholder={
              handleInsertTimeUntilEventPlaceholder
            }
            showTimeUntilButton
            showSendNowButton={false}
            subjectInputRef={subjectInputRef}
            bodyTextareaRef={bodyTextareaRef}
            isActionsDisabled={!!timeValidationError}
            subjectLimit={120}
            bodyLimit={2000}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
